import { Head, Link, useForm } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Reply {
  id: number
  user: string
  notes: string
  created_at: string
}
interface Notification {
  id: number
  user_id: number
  user: string
}
interface Todo {
  id: number
  title: string
  description: string | null
  due_date: string | null
  manager: string | null
  added_by: string | null
  status: number
  overdue: boolean
  completed_at: string | null
  completed_by: string | null
  completion_notes: string | null
  can_complete: boolean
  notifications: Notification[]
  replies: Reply[]
}
interface UserOption {
  id: number
  name: string
}
interface Props {
  todo: Todo
  users: UserOption[]
}

export default function Show({ todo, users }: Props) {
  const [showCompleteForm, setShowCompleteForm] = useState(false)
  const [showFollowUp, setShowFollowUp] = useState(false)
  const [confirmComplete, setConfirmComplete] = useState(false)

  const completeForm = useForm({
    marketing_todo: { completion_notes: "" },
    follow_up: {
      title: "",
      description: "",
      due_date: "",
      manager_id: "",
    },
  })

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Marketing Todos", href: "/marketing_todos" },
    { title: todo.title, href: `/marketing_todos/${todo.id}` },
  ]

  function handleComplete(e: React.FormEvent) {
    e.preventDefault()
    completeForm.post(`/marketing_todos/${todo.id}/complete`)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={todo.title} />
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            {todo.title}
            {todo.overdue && (
              <span className="text-hpi-red ml-2 text-sm">Overdue</span>
            )}
            {todo.status === 1 && (
              <span className="text-hpi-green ml-2 text-sm">Completed</span>
            )}
          </h1>
          <div className="flex gap-2">
            {todo.can_complete && (
              <button
                type="button"
                onClick={() => setShowCompleteForm(!showCompleteForm)}
                className="bg-hpi-green hover:bg-hpi-green/80 rounded px-4 py-2 text-white"
              >
                Complete Task
              </button>
            )}
            <Link
              href={`/marketing_todos/${todo.id}/edit`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Edit
            </Link>
          </div>
        </div>

        {todo.completed_at && (
          <div className="mb-4 rounded border border-green-200 bg-green-50 p-3">
            <p className="font-semibold">
              Completed on {formatDate(todo.completed_at)} by{" "}
              {todo.completed_by}
            </p>
            {todo.completion_notes && (
              <p className="mt-1 whitespace-pre-wrap">
                {todo.completion_notes}
              </p>
            )}
          </div>
        )}

        {showCompleteForm && (
          <form
            onSubmit={handleComplete}
            className="mb-6 rounded border bg-gray-50 p-4"
          >
            <h3 className="mb-3 text-lg font-semibold">Complete Task</h3>
            <div className="mb-3">
              <label className="mb-1 block font-semibold">
                Completion Notes <span className="text-hpi-red">*</span>
              </label>
              <textarea
                className="w-full rounded border p-2"
                rows={4}
                required
                value={completeForm.data.marketing_todo.completion_notes}
                onChange={(e) =>
                  completeForm.setData("marketing_todo", {
                    ...completeForm.data.marketing_todo,
                    completion_notes: e.target.value,
                  })
                }
              />
            </div>

            <div className="mb-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={confirmComplete}
                  onChange={(e) => setConfirmComplete(e.target.checked)}
                  required
                />
                Confirm that this task has been completed
              </label>
            </div>

            <div className="mb-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showFollowUp}
                  onChange={(e) => setShowFollowUp(e.target.checked)}
                />
                Schedule a follow-up task
              </label>
            </div>

            {showFollowUp && (
              <div className="mb-3 rounded border bg-white p-3">
                <h4 className="mb-2 font-semibold">Follow-Up Task</h4>
                <div className="mb-2">
                  <label className="mb-1 block text-sm font-medium">
                    Title
                  </label>
                  <input
                    type="text"
                    className="w-full rounded border p-2"
                    maxLength={50}
                    value={completeForm.data.follow_up.title}
                    onChange={(e) =>
                      completeForm.setData("follow_up", {
                        ...completeForm.data.follow_up,
                        title: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="mb-2">
                  <label className="mb-1 block text-sm font-medium">
                    Assigned To
                  </label>
                  <select
                    className="w-full rounded border p-2"
                    value={completeForm.data.follow_up.manager_id}
                    onChange={(e) =>
                      completeForm.setData("follow_up", {
                        ...completeForm.data.follow_up,
                        manager_id: e.target.value,
                      })
                    }
                  >
                    <option value="">Select employee...</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-2">
                  <label className="mb-1 block text-sm font-medium">
                    Due Date
                  </label>
                  <input
                    type="date"
                    className="w-full rounded border p-2"
                    value={completeForm.data.follow_up.due_date}
                    onChange={(e) =>
                      completeForm.setData("follow_up", {
                        ...completeForm.data.follow_up,
                        due_date: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="mb-2">
                  <label className="mb-1 block text-sm font-medium">
                    Instructions
                  </label>
                  <textarea
                    className="w-full rounded border p-2"
                    rows={3}
                    value={completeForm.data.follow_up.description}
                    onChange={(e) =>
                      completeForm.setData("follow_up", {
                        ...completeForm.data.follow_up,
                        description: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={completeForm.processing || !confirmComplete}
              className="bg-hpi-green hover:bg-hpi-green/80 rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {completeForm.processing ? "Completing..." : "Confirm Completion"}
            </button>
          </form>
        )}

        <dl className="mb-6 grid grid-cols-2 gap-4">
          <div>
            <dt className="font-semibold">Due Date</dt>
            <dd>{todo.due_date ?? "Not set"}</dd>
          </div>
          <div>
            <dt className="font-semibold">Manager</dt>
            <dd>{todo.manager}</dd>
          </div>
          <div className="col-span-2">
            <dt className="font-semibold">Description</dt>
            <dd className="whitespace-pre-wrap">{todo.description}</dd>
          </div>
          <div className="col-span-2">
            <dt className="font-semibold">Also Notified</dt>
            <dd>
              {todo.notifications.length > 0
                ? todo.notifications.map((n) => n.user).join(", ")
                : "None"}
            </dd>
          </div>
        </dl>
        <div className="mb-6">
          <h2 className="mb-2 text-lg font-semibold">Replies</h2>
          <Link
            href={`/marketing_todos/${todo.id}/replies/new`}
            className="text-hpi-active-blue hover:text-hpi-active-blue mb-2 inline-block"
          >
            Add Reply
          </Link>
          {todo.replies.length > 0 ? (
            <div className="space-y-2">
              {todo.replies.map((r) => (
                <div key={r.id} className="rounded border p-2">
                  <strong>{r.user}</strong> - {r.created_at}
                  <p>{r.notes}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-hpi-med-gray">No replies yet.</p>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
